/**
 * UPG Base Node and shared primitives. Every node extends `UPGBaseNode`.
 * https://unifiedproductgraph.org/spec | MIT
 */

import type { UPGEntityType } from '../catalog/entity-catalog.js'
import type { ISODateTime } from '../properties/primitives.js'

// ─── Shared primitives ────────────────────────────────────────────────────────

/** Confidence level for a type mapping when importing from an external tool.
 *  - `high`: unambiguous match (e.g. exact type string match)
 *  - `medium`: probable match (e.g. semantic similarity)
 *  - `low`: speculative match, human review recommended
 *  - `manual`: mapping was set explicitly by a human */
export type UPGMappingConfidence = 'high' | 'medium' | 'low' | 'manual'

/** One external link beyond the canonical artifact.
 *
 * Generalises the `ServiceProperties.links` shape, which was the only
 * list-of-links declaration in the spec before 0.33.0, up to the base node so
 * that any node can hold more than one outward pointer.
 */
export interface UPGExternalLink {
  /** URI. Same posture as `UPGBaseNode.external_ref`: https:// for cloud tools,
   *  file:// or a relative path for local files. */
  url: string
  /** Human label for the link. */
  label?: string
  /** What kind of thing is on the other end, e.g. "pull_request", "design",
   *  "runbook". A free string with a documented convention rather than an enum:
   *  the set is open by nature and a closed one would be wrong within a release. */
  kind?: string
}

// ─── Base node ────────────────────────────────────────────────────────────────

/** The structural base shared by every node in a product graph.
 *
 * All entity types extend this interface, either directly (using `properties`
 * as `Record<string, unknown>`) or via the typed `UPGNode<T>` wrapper, which
 * narrows `properties` to the correct interface from `UPGPropertyMap`.
 *
 * Core identity fields (`id`, `type`, `title`) are required.
 * Everything else is optional to keep the format lightweight.
 *
 * @example
 * // A minimal persona node, only required fields populated.
 * const persona: UPGBaseNode = {
 *   id: 'n_persona_1',
 *   type: 'persona',
 *   title: 'Head of Product at a B2B SaaS scale-up',
 * }
 *
 * @example
 * // An imported node with mapping metadata + type-specific properties.
 * const importedPersona: UPGBaseNode = {
 *   id: 'n_persona_2',
 *   type: 'persona',
 *   title: 'Solo founder, non-technical',
 *   description: 'Operator who ships with AI and needs thinking tools to keep up.',
 *   tags: ['primary', 'launch-audience'],
 *   source_id: 'notion_page_abc123',
 *   source_type: 'customer_archetype',
 *   mapping_confidence: 'high',
 *   external_tool: 'notion',
 *   external_ref: 'https://notion.so/acme/abc123',
 *   properties: {
 *     is_primary: true,
 *     experience_level: 'intermediate',
 *   },
 * }
 */
export interface UPGBaseNode {
  /** Unique identifier within the graph */
  id: string
  /** The UPG entity type (must be a value from UPGEntityType) */
  type: UPGEntityType
  /** Human-readable title */
  title: string
  /**
   * Stable, human-readable handle for inline `[[type:slug]]` chips in
   * `.upg.md` documents. Auto-generated from `title` when omitted; unique
   * within `(product_id, type)`. The `id` field remains the canonical
   * identifier for adapters, MCP tools, and cross-product edges. Resolvers
   * MUST accept either form (UUID `id` OR slug) when matching chips.
   */
  slug?: string
  /**
   * Past values of `slug`, retained when the slug is renamed so existing
   * `.upg.md` chips that reference the old slug still resolve. The set
   * (slug ∪ aliases) is unique within `(product_id, type)`. Order is
   * preservation-only; resolvers treat aliases as a flat lookup set.
   */
  aliases?: string[]
  /**
   * Stable, human-citable key minted for this node (e.g. `"LTN-311"`). Unique
   * within the product ACROSS entity types, immutable once assigned, and never
   * reused. Distinct from `slug`: a key is minted, not derived from the title.
   *
   * @remarks
   * WHY NOT `slug`. A slug is unique within `(product_id, type)` and is
   * auto-generated from `title` when omitted. A citable key is neither: under
   * slug's scope a `task` and a `bug` could each legally hold `LTN-311`, and a
   * key that followed a retitle would break every citation that made it worth
   * having. The two fields answer different questions and both are optional.
   *
   * WHY NOT `external_id`. That field records the identifier a node had in the
   * tool it came FROM. It is the right home for an imported key's provenance
   * and is structurally incapable of naming the next node, because there is no
   * external tool to mint it. A graph that outlives its source tool needs a key
   * of its own.
   *
   * MINTING. The next number is `max(existing) + 1`, derived from the graph. No
   * counter is serialised, because a counter is store state rather than a fact
   * about the thing (the same cut that keeps `composition.rev`, which is a fact,
   * and excludes a concurrency token, which is not).
   *
   * WHICH PREFIX (normative; ladder stated as built in 0.33.1, candidate set
   * narrowed in 0.34.0). Three rungs, tried in order.
   *
   *   1. A create NAMES its prefix explicitly.
   *   2. Otherwise the CANDIDATE SET is consulted. It is the UNION of the prefixes
   *      DECLARED by the product's teams (`team.key_prefix`) and the prefixes
   *      OBSERVED on the product's existing keys. One candidate resolves; more
   *      than one, and the create surface asks. `product.key_prefix` is consulted
   *      only when no team declares one, and is `@deprecated` for that reason.
   *   3. A product with an empty candidate set and no explicit prefix mints no
   *      keys.
   *
   * THE OBSERVED RUNG IS A REAL MINT PATH, and 0.33.0 published two sentences that
   * disagreed about whether it existed. This paragraph used to end "A product where
   * nothing declares a prefix mints no keys", which deletes it; the two paragraphs
   * below both name a prefix INFERRED from existing keys, and `team.key_prefix`
   * rule 3 presupposes it by construction. The field settles it: the only keyed
   * graph in the estate carries 1,032 keys minted by inference in a product that
   * declares no prefix at all, and under the deleted rung that graph could not
   * exist. Corrected in the 0.33.1 patch and restated here.
   *
   * A DECLARATION ADDS A CANDIDATE; IT DOES NOT REMOVE ONE (0.34.0). Declaring a
   * team prefix suppresses `product.key_prefix` and nothing else. It never
   * suppresses an OBSERVED prefix, which is evidence of a namespace already in
   * use. The full argument, the measurement behind it and the reason it ships as a
   * change rather than as a patch are on `team.key_prefix`.
   *
   * Key uniqueness is scoped to the product across entity types; a prefix names a
   * team within that scope, not a separate number line.
   *
   * ENFORCED WITHIN THE PRODUCT (0.34.1). A create naming a key another node in
   * this product already holds is REFUSED, across entity types, with a typed
   * `DuplicateNodeKeyError`. Immutability was already enforced on the update
   * path from 0.33.0, through the typed field and through `properties` both.
   *
   * THE REFUSAL IS AN ERROR AND NOT A SILENT NO-KEY, and the distinction is the
   * one the minting rules draw two paragraphs down. A portfolio-shared team's
   * prefix mints nothing in a second product, and there the refusal IS no-key
   * and never an exception — because nobody asked for that key; it was inferred.
   * A duplicate arrives the other way round: the caller NAMED it. Returning
   * success for a node whose citation was quietly dropped is the failure mode
   * the 0.32.2 field guards exist to end.
   *
   * ENFORCED ON CREATE, NOT ON OPEN. A graph minted before the check existed may
   * hold a collision, and a store that refuses to load it leaves its owner no
   * way to repair it. The index is therefore built first-wins at load and the
   * invariant is held at the only moment it can still be honoured.
   *
   * THE PRECEDING TWO PARAGRAPHS SHIPPED AS AN OVERCLAIM AND ARE CORRECTED HERE.
   * 0.33.0 wrote "uniqueness is enforced per product (the store index is
   * `(product_id, key)`)" as settled fact, and 0.34.0 restated the within-product
   * case as stated-but-unchecked without reconciling the two. Neither was
   * describing anything that ran: no index existed and no check fired, on any
   * release through 0.34.0. It was measured on the only keyed graph in the
   * estate — 1,032 keys, and a second node claiming one of them was accepted
   * without a warning. This matters more than a wrong sentence usually would,
   * because the portfolio detector below names the per-product invariant as its
   * premise: a portfolio check that assumes each product is internally clean was
   * resting on nothing. 0.34.1 makes the sentence true rather than deleting it.
   *
   * PORTFOLIO-WIDE UNIQUENESS IS DEFINED AND UNENFORCED (0.33.0). Within a
   * portfolio, one `(prefix, number)` pair should identify one node. Uniqueness
   * is enforced per product (the store index is `(product_id, key)`, and from
   * 0.34.1 that index exists), so two products minting under one prefix produce
   * the same citation for two different things. This is measured rather than
   * feared: a fixture reproduces the
   * collision through the ordinary create path, including the quiet case where
   * nobody declares a prefix and it is inferred from existing keys. No PER-PRODUCT
   * check can ever see it, because each product reports its own key as valid —
   * which is why the detector that ships in 0.34.0 is a PORTFOLIO-scope
   * anti-pattern (`duplicate-key-across-products`) rather than a graph validation.
   * It is ENFORCED from the day it ships; what was staged is the corpus, and the
   * distinction matters because there is no defined-then-enforced mechanic and
   * inventing one would have been worse than either real option. A detector that is
   * registered but declines to fire is a detector nobody can reason about.
   *
   * MINTING IS PRODUCT-SCOPED (normative, 0.33.0). The `(product_id, key)` index
   * is the enforced invariant and the scope of a key sequence is the product.
   * `team` is `portfolio_shared`, so one team can be referenced from two
   * products; minting does not travel with it. A portfolio-shared team's prefix
   * is NOT a minting candidate in a second product, refusal is NO-KEY and never
   * an exception, and the rule applies on the MINT path including a prefix that
   * was INFERRED from existing keys rather than requested by any caller.
   * Portfolio-shared team minting is deferred until a supra-product uniqueness
   * design exists. See `team.key_prefix` for the rule in full, including the three
   * mechanics 0.34.0 made normative that this rule needs and 0.33.0 left open: HOW
   * a minter decides which product a prefix belongs to (evidence, not a stored
   * marker), what happens when nothing can decide (NEITHER mints, no invented
   * tiebreak), and what "in scope" means (engine-defined, with a floor at every
   * product the engine can enumerate for this caller and a ceiling at every product
   * the caller could not otherwise read).
   *
   * WHERE THE BEHAVIOUR LIVES, because this is a contract and not an
   * implementation. Nothing in this package mints a key. Deriving the next number
   * requires reading every keyed node in the product, and that read must page
   * explicitly, so the minter is the graph service and the agent surface accepts
   * a caller-supplied key on create without ever deriving one.
   *
   * @example "LTN-311"
   */
  key?: string
  /** Optional narrative description */
  description?: string
  /** Freeform tags for filtering and grouping */
  tags?: string[]
  /** Current lifecycle phase (must be a phase ID from getLifecycleForType()).
   *  E.g., for hypothesis: 'untested' | 'testing' | 'resolved'.
   *  Entity types without a lifecycle definition should omit this field.
   *  Validated at runtime against UPG_ALL_PHASES_SET. */
  status?: string
  /**
   * Swept out of default views. ORTHOGONAL to `status`: a node can be done and
   * live, or done and archived, and those are different facts. Archived nodes
   * remain fully queryable.
   *
   * @remarks
   * WHY THIS IS NOT A LIFECYCLE PHASE. Several lifecycles carry an `archived`
   * phase, and in every one of them it is `status_category: 'completed'` — so in
   * the six-bucket read, archived and done are indistinguishable. Field data
   * settles it: a real 1,032-issue tracker held 559 archived-Done items
   * alongside 18 live-Done ones. One field cannot carry two facts, and a bucket
   * system that collapses them cannot answer the question it exists for.
   *
   * THE DEFAULT-READ CONVENTION, documented and NOT enforced: archived nodes are
   * excluded from default views and included on request (`UPGViewQuery
   * .include_archived`). A consumer that shows them by default is doing
   * something unusual, not something wrong, so no check fires on it.
   *
   * Generalised at 0.32.0 from `WorkspaceProperties.archived`, which shipped the
   * same pair for one type and is now `@deprecated` in favour of this field.
   * The existing `archived` LIFECYCLE PHASES are deliberately untouched;
   * reconciling them is its own cycle.
   */
  archived?: boolean
  /** ISO timestamp archived. Pairs with `archived === true`. */
  archived_at?: ISODateTime
  /** Original ID in the source tool (for round-trip fidelity) */
  source_id?: string
  /** Original type name in the source tool */
  source_type?: string
  /** Confidence level of the type mapping */
  mapping_confidence?: UPGMappingConfidence
  /** External tool that holds the canonical artifact (e.g. "figma", "linear", "notion") */
  external_tool?: string
  /** URI to the canonical artifact: https:// for cloud tools, file:// or relative path for local files */
  external_ref?: string
  /** Identifier in the external tool's system (for sync / round-trip) */
  external_id?: string
  /**
   * Additional external links. `external_ref` names THE canonical artifact and
   * stays the single answer to "where does this live"; this list holds
   * everything else that points outward. A node may carry this with no
   * `external_ref` when no single link is canonical.
   *
   * @remarks
   * WHY A LIST AND NOT AN ENTITY. A measured tracker import produced 703
   * attachments across 1,032 issues: 524 fitted on `external_ref` and 162
   * overflowed into a vendor-namespaced property bag key with nowhere declared to
   * go. A link has no lifecycle, no owner, no description and no independent
   * identity, so minting a `link` type for it would pay roughly fourteen
   * registration points for a scalar fact about a node.
   *
   * WHY NOT `external_refs`. That name is one character from `external_ref` and
   * means the opposite thing (all the others, versus the canonical one). Two
   * fields whose names differ by a plural and whose semantics differ by
   * canonicality is a misreading waiting to happen in every consumer.
   *
   * MIGRATION NOTE, CORRECTED 0.34.0 ON TWO FIGURES. Both were carried forward
   * from the 0.33.0 pass and both were wrong, and the second would have broken
   * this field's own contract on its first real population.
   *
   *   THE RESIDUE IS 179, NOT 162. Seventeen further attachments sat in a second
   *   vendor bag key and were equally homeless. They were never counted because
   *   the census read one key.
   *
   *   THE CANONICAL URL MUST BE SUBTRACTED BEFORE THE COPY. 524 of the 528 nodes
   *   holding a parked bag DUPLICATE their own `external_ref` inside it. A
   *   straight copy of the bag into this list would therefore put the canonical
   *   artifact into the NON-canonical list on 99% of the field's first real
   *   population, breaking the exact invariant this field was minted to
   *   establish. A migration reads the bag, removes the entry matching
   *   `external_ref`, and copies what remains.
   *
   * The correction is recorded here rather than in a plan because this is where a
   * migration author will be standing when they need it.
   *
   * @example
   * [{ url: 'https://github.com/acme/api/pull/812', label: 'PR 812', kind: 'pull_request' }]
   */
  external_links?: UPGExternalLink[]
  /**
   * When the node was created. Store metadata lifted into the spec at 0.33.0 so
   * that a declared view query can express a window over it.
   *
   * @volatile
   * @remarks
   * TAGGED `@volatile` DELIBERATELY. These two timestamps are maintained by
   * whatever store holds the graph rather than authored, so they are not design
   * knowledge and a reader must not treat them as stable facts about the thing.
   * They are declared anyway because `UPGViewClause` can open a `date` window and
   * two of the six date dimensions a real board filters on are these; leaving them
   * undeclared would ship a query language that cannot say what the surface most
   * often says, which is the failure the clause list exists to fix.
   */
  created_at?: ISODateTime
  /**
   * When the node was last modified. Store metadata, same posture as
   * `created_at`.
   *
   * @volatile
   */
  updated_at?: ISODateTime
  /**
   * Type-specific properties.
   *
   * @remarks
   * NAMESPACED EXTENSION KEYS (the 0.31.0 rule, extended to this bag at 0.33.0).
   * A key that a tool owns and the spec does not declare is written
   * `<tool>:<key>`, with a colon. An underscore key such as
   * `linear_state_history` is indistinguishable from a misspelled spec property:
   * no migration can target it and no validator can tell it apart from a typo,
   * and that undetectability is the whole reason the rule exists. The rule was
   * written for `WorkspaceCanvas` and applies here for the same reason.
   *
   * ONE UNDECLARED EXTENSION IS KNOWN, MEASURED AND DELIBERATELY LEFT UNDECLARED.
   * A tracker import carries raw ordered state-transition history for every issue
   * (1,032 issues, 2,862 transitions in the measured corpus) under a vendor-owned
   * key. It has no declared shape because nothing reads it: declaring a shape for
   * records nobody queries is dead schema, and it would freeze one vendor's
   * transition model into the format before a second source has been seen. The
   * pull-forward condition is the first reader. Silence and a decision look
   * identical six months later, so this is written down as a decision.
   *
   * THAT KEY IS NOT ACTUALLY NAMESPACED, AND THE PARAGRAPH ABOVE USED TO CLAIM IT
   * WAS (corrected 0.34.1). The rule two paragraphs up mandates `<tool>:<key>`
   * with a COLON. The import writes `linear_state_history`, with an underscore —
   * the precise shape the rule was written to forbid, cited here as the example
   * of compliance. It went unnoticed because nothing could see it: no drift class
   * examined undeclared bag keys until `undeclared_property_drift` shipped in
   * 0.34.1, which measured 5,779 such keys across eight `linear_*` shapes on the
   * one imported graph in the estate.
   *
   * Recorded rather than migrated, because renaming a key on 1,032 nodes is a
   * migration with its own cycle and this paragraph is where its author will be
   * standing. What changes here is the claim, not the data.
   */
  properties?: Record<string, unknown>
}

// ─── Base-node fields as runtime data ─────────────────────────────────────────

/**
 * Compile-time lock between `UPGBaseNode` and its runtime field list.
 *
 * `Record<keyof UPGBaseNode, true>` is exhaustive in BOTH directions: omit a
 * field and TypeScript reports the property as missing; add one that is not on
 * the interface and it reports an excess property. So a field cannot be added
 * to `UPGBaseNode` without this object failing to compile until it is listed
 * here too, and the runtime list below can never quietly disagree with the type.
 *
 * WHY THIS EXISTS. A TypeScript interface is erased at runtime, so every
 * consumer that needs to ask "is this key a base field?" previously kept its own
 * hand-maintained copy, each carrying a comment asking the next editor to keep
 * it in sync. Three such copies existed and 0.32.0 updated none of them: `key`,
 * `archived`, and `archived_at` shipped on the interface while
 * `UPGFileStore.updateNode` silently dropped all three from any patch and two
 * drift detectors reported a node holding them as carrying "non-spec top-level
 * fields". Derivation replaces the request to remember with a build failure.
 *
 * The value is `true` throughout and carries no meaning; only the KEYS matter.
 */
const BASE_NODE_FIELD_PRESENCE: Record<keyof UPGBaseNode, true> = {
  id: true,
  type: true,
  title: true,
  slug: true,
  aliases: true,
  key: true,
  description: true,
  tags: true,
  status: true,
  archived: true,
  archived_at: true,
  source_id: true,
  source_type: true,
  mapping_confidence: true,
  external_tool: true,
  external_ref: true,
  external_id: true,
  external_links: true,
  created_at: true,
  updated_at: true,
  properties: true,
}

/**
 * Every top-level field declared by `UPGBaseNode`, as runtime data.
 *
 * This is the SINGLE SOURCE for "what is a base-node field". Consumers that
 * classify top-level keys (drift detectors, patch mergers, serialisers) must
 * derive from this rather than enumerate their own list.
 *
 * Order follows the interface declaration order and is not significant; callers
 * that need a canonical serialisation order use `NODE_KEY_ORDER` in
 * `format/canonical.ts`, which is deliberately a SUPERSET (it also orders
 * tolerated non-base keys such as `lifecycle_status` and `sort_order`).
 */
export const UPG_BASE_NODE_FIELDS: readonly (keyof UPGBaseNode)[] = Object.freeze(
  Object.keys(BASE_NODE_FIELD_PRESENCE) as (keyof UPGBaseNode)[],
)

/** `UPG_BASE_NODE_FIELDS` as a lookup set, for membership tests. */
export const UPG_BASE_NODE_FIELD_SET: ReadonlySet<string> = new Set<string>(UPG_BASE_NODE_FIELDS)

/**
 * Base-node fields that `id` aside are NOT freely mergeable by a generic
 * shallow patch, because each needs its own handling:
 *
 *   - `id`         identity; changing it would orphan every edge.
 *   - `type`       narrowed to `UPGEntityType`, and a type change carries a
 *                  property migration, so it routes through a migration path.
 *   - `slug`       a change rotates the old value into `aliases` (see
 *                  `rotateSlug`), so it is never a plain assignment.
 *   - `aliases`    replaced outright when patched directly, which must happen
 *                  AFTER the slug rotation that would otherwise append to it.
 *   - `properties` deep-merged rather than replaced.
 *
 * Exported so a merger can state the exclusions once and derive the rest.
 */
export const UPG_BASE_NODE_SPECIAL_MERGE_FIELDS: ReadonlySet<string> = new Set<string>([
  'id',
  'type',
  'slug',
  'aliases',
  'properties',
])
